import { motion } from "framer-motion";
import { Plane, MapPin, Clock, Car, CheckCircle2, Phone } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import SectionHeading from "@/components/SectionHeading";
import BookingForm from "@/components/BookingForm";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const services = [
  {
    icon: MapPin,
    title: "Pondicherry Local Sightseeing Taxi & City Tour Packages",
    desc: "Explore Pondicherry with our specialized local sightseeing taxi and city tour packages. Visit the French Colony, Auroville, Promenade Beach, and Paradise Beach with expert drivers who know every hidden gem. Best Pondicherry sightseeing cab service available daily.",
    features: ["French Quarter Tour", "Auroville Visit", "Beach Hopping", "Temple Tours", "Custom Itineraries", "Half/Full Day"],
  },
  {
    icon: Plane,
    title: "Pondicherry Airport Taxi — Chennai Airport Drop & Pickup",
    desc: "Reliable Pondicherry airport taxi service for Chennai airport drop and pickup (MAA). Our Chennai airport drop taxi service includes flight tracking and meet-and-greet for a stress-free airport transfer from hotels in Pondicherry.",
    features: ["Chennai Airport", "Pondicherry Airport", "Flight Tracking", "Meet & Greet", "24/7 Availability", "Luggage Support"],
  },
  {
    icon: Car,
    title: "Outstation Cab Pondicherry — 24/7 Outstation Taxi Service",
    desc: "Comfortable 24/7 outstation cab Pondicherry for long-distance travel to Bangalore, Tirupati, Madurai, and Rameshwaram. Book Pondicherry to Chennai cab or any South India destination with experienced highway drivers.",
    features: ["Round Trips", "Multi-day Tours", "Safe Highway Travel", "Interstate Permits", "All-India Service", "Flexible Returns"],
  },
  {
    icon: Clock,
    title: "24/7 Daily Pickup Drop Service — Local Cab Pondicherry",
    desc: "Fast and convenient daily pickup drop service and point-to-point cab near me Puducherry. From railway station to hotel transfers, our drop service Pondicherry runs around the clock. Reliable puducherry cab service for all needs.",
    features: ["Railway Station", "Bus Stand", "Hotel Transfers", "Quick Bookings", "Safe & Secure", "Doorstep Pickup"],
  },
  {
    icon: MapPin,
    title: "One-Way Drop Taxi Pondicherry — Affordable One-Way Cab",
    desc: "Save up to 50% with our one-way drop taxi service in Pondicherry to Chennai, Bangalore, and Coimbatore. Our one-way cab Pondicherry charges only for the distance you travel — no return fare. Best Pondicherry to Chennai taxi rates.",
    features: ["Transparent Pricing", "No Return Fare", "Intercity Travel", "All Major Cities", "Fixed Rates", "Comfortable Rides"],
  },
  {
    icon: Car,
    title: "Corporate Cab Service Pondicherry — Office Pickup Drop Taxi",
    desc: "Premium corporate cab service in Pondicherry for businesses and events. We provide office pickup drop taxi, employee commutes, and luxury client transfers. Trusted tours and travels Pondicherry partner for all corporate fleets.",
    features: ["Monthly Billing", "Priority Booking", "High-End Fleet", "Professional Drivers", "Event Transport", "Corporate Rates"],
  },
];

const Services = () => (
  <Layout>
    <SEOHead
      title="Best Taxi Services in Pondicherry | 24/7 Cab Booking — Taxi Near Me Pondicherry"
      description="Best taxi service in Pondicherry — cab near me Puducherry for local sightseeing, Pondicherry airport taxi service, Chennai airport drop, one-way drop taxi & outstation cab. Affordable 24/7 cab booking Pondicherry."
      keywords="best taxi service in Pondicherry, Pondicherry taxi booking, Chennai airport taxi Pondicherry, outstation cab Pondicherry, local sightseeing Pondy, reliable taxi Pondicherry, taxi in Pondicherry, cab booking Pondicherry, cheap taxi Puducherry, best taxi services in pondicherry, taxi services in pondicherry, 24/7 taxi services, Affordable Taxi Services in Pondicherry, Affordable Taxi Fares in Pondicherry, Pondicherry local sightseeing taxi, Pondicherry city tour packages, Pondicherry sightseeing cab service, Pondicherry airport taxi service, Airport pickup and drop Pondicherry, Chennai to Pondicherry airport taxi, Pondicherry to Chennai cab, Daily pickup drop service, Office pickup drop taxi, Chennai Airport drop taxi service, Pondicherry Airport drop Taxi service, Tours and travels in pondicherry, One-way drop taxi service in pondicherry, Local Pondicherry sightseeing(City Tour), Airport transfer (Hotels)pondicherry, Outstation trips pondicherry, corporate cab service in pondicherry,Book Taxi in Pondicherry Online, Pondicherry Cab Service Near Me, Private Cab Service Pondicherry, Round Trip Taxi Service Pondicherry, Online Cab Booking Pondicherry"
      canonical="https://pondicherry.taxi/services"
    />

    {/* Hero */}
    <section className="pt-28 pb-16 section-padding bg-gradient-to-b from-primary/5 to-transparent">
      <div className="container-custom text-center">
        <motion.h1 {...fadeUp} className="text-4xl sm:text-5xl font-bold">
          Best <span className="text-gradient">Taxi Service & Cab Booking</span> in Pondicherry
        </motion.h1>
        <motion.p {...fadeUp} className="mt-4 text-muted-foreground max-w-2xl mx-auto text-lg">
          Experience premium and affordable taxi near me Pondicherry with our top-rated cab service. Whether you need a one-way drop taxi, Pondicherry airport taxi service, daily pickup drop, or a 24/7 outstation cab Pondicherry — we ensure safe and comfortable journeys every time.
        </motion.p>
      </div>
    </section>

    {/* Services */}
    <section className="section-padding">
      <div className="container-custom space-y-16">
        {services.map((s, i) => (
          <motion.div
            key={s.title}
            {...fadeUp}
            transition={{ delay: 0.1 }}
            className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "md:direction-rtl" : ""}`}
          >
            <div className={i % 2 === 1 ? "md:order-2" : ""}>
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                <s.icon className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">{s.title}</h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">{s.desc}</p>
              <div className="grid grid-cols-2 gap-3">
                {s.features.map((f) => (
                  <span key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" /> {f}
                  </span>
                ))}
              </div>
              <a href="tel:+919952493907" className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-3">
                <Phone className="w-4 h-4" /> Book {s.title}
              </a>
            </div>
            <div className={`glass-card rounded-2xl p-10 flex items-center justify-center min-h-[300px] ${i % 2 === 1 ? "md:order-1" : ""}`}>
              <s.icon className="w-24 h-24 text-primary/20" />
            </div>
          </motion.div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="section-padding bg-taxi-darker/50">
      <div className="container-custom max-w-2xl text-center">
        <SectionHeading
          badge="Instant Booking"
          title="Book Your"
          highlight="Pondicherry Taxi & Cab Service"
          description="Ready for a safe ride? Fill the form or call to book cab near me Puducherry instantly — local sightseeing, airport taxi service, one-way drop taxi & outstation cab at the best rates."
        />
        <BookingForm idPrefix="services" />
      </div>
    </section>
  </Layout>
);

export default Services;
